import React from 'react';
import {
  Box, Container, Text, Stack, Avatar, Flex, 
  useColorModeValue, SimpleGrid, Icon, Badge, VStack
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { testimonials } from './data'; 
import AnimatedUnderline from '../Underlines/AnimatedUnderline1';
import { FaQuoteLeft } from 'react-icons/fa';

const MotionDiv = motion(Box);

// Animation wrapper for the vertical scroll
const MarqueeColumn = ({ items, duration, reverse = false }) => {
  return (
    <Box 
      height="650px" 
      overflow="hidden" 
      position="relative" 
      px={2}
      cursor="pointer"
    >
      <MotionDiv
        initial={{ y: reverse ? "-50%" : "0%" }}
        animate={{ y: reverse ? "0%" : "-50%" }}
        transition={{
          duration: duration, // Increased for slower speed
          repeat: Infinity,
          ease: "linear",
        }}
        // PAUSE ON HOVER: This makes it user-friendly
        whileHover={{ animationPlayState: "paused" }}
      >
        {[...items, ...items].map((item, index) => (
          <TestimonialCard key={index} {...item} />
        ))}
      </MotionDiv>
    </Box>
  );
};

const TestimonialCard = ({ name, review, place, image, rating }) => {
  const cardBg = useColorModeValue("white", "gray.900");
  const borderColor = useColorModeValue("gray.100", "whiteAlpha.200");
  const textColor = useColorModeValue("gray.600", "gray.400");

  return (
    <Box
      bg={cardBg}
      p={8}
      mb={8}
      borderRadius="3xl"
      border="1px solid"
      borderColor={borderColor}
      boxShadow="xl"
      transition="all 0.3s"
      _hover={{ boxShadow: "2xl", transform: "translateY(-4px)" }}
    >
      <Icon as={FaQuoteLeft} color="#F59E0B" mb={4} boxSize={6} opacity={0.4} />
      <Text fontSize="lg" color={textColor} mb={8} lineHeight="1.6" fontWeight="500">
        "{review}"
      </Text>
      
      <Flex align="center" gap={4}>
        <Avatar src={image} name={name} size="md" border="2px solid" borderColor="#F59E0B" />
        <VStack align="flex-start" spacing={0}>
          <Text fontWeight="800" fontSize="md">{name}</Text>
          <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase">
            {place}
          </Text>
        </VStack>
        <Badge ml="auto" variant="subtle" colorScheme="yellow" px={2} borderRadius="md">
          {rating} / 5
        </Badge>
      </Flex>
    </Box>
  );
};

const TestimonialSlider = () => {
  const headingColor = useColorModeValue("black", "white");
  const subTextColor = useColorModeValue("gray.600", "gray.400");
  const fadeGradientTop = useColorModeValue(
    "linear(to-b, white, transparent)", 
    "linear(to-b, gray.800, transparent)"
  );
  const fadeGradientBottom = useColorModeValue(
    "linear(to-t, white, transparent)", 
    "linear(to-t, gray.800, transparent)"
  );

  const midIndex = Math.ceil(testimonials.length / 2);
  const firstCol = testimonials.slice(0, midIndex);
  const secondCol = testimonials.slice(midIndex);

  return (
    <Box py={24} position="relative" overflow="hidden">
      <Container maxW="7xl">
        <Stack spacing={4} align="center" mb={20}>
          <Text
            fontSize={["4xl", "5xl", "8xl"]}
            fontWeight="900"
            lineHeight="0.9"
            color={headingColor}
            fontFamily="Bricolage Grotesque"
            textAlign="center"
            letterSpacing="tighter"
          >
            Real Stories. <br />
            <span style={{ color: "#F59E0B" }}>Zero Fluff.</span>
          </Text>
          <AnimatedUnderline />
         <Text
    textAlign="center"
    maxW="3xl"
    fontSize="md"
    color={subTextColor}
    opacity={0.8}
  >
    From "Hello World" to high-figure offers. Our community isn't just learning—they're leveling up. See how GrapplTech alumni are disrupting the industry, securing the bag, and building the future they actually want.
  </Text>
        </Stack>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={12} position="relative">
          {/* Edge Masks */}
          <Box position="absolute" top={-2} left={0} right={0} h="120px" bgGradient={fadeGradientTop} zIndex={5} pointerEvents="none" />
          <Box position="absolute" bottom={-2} left={0} right={0} h="120px" bgGradient={fadeGradientBottom} zIndex={5} pointerEvents="none" />

          {/* Speed settings: Higher number = Slower scroll */}
          <MarqueeColumn items={firstCol} duration={80} />
          <Box display={{ base: "none", md: "block" }}>
            <MarqueeColumn items={secondCol} duration={100} reverse={true} />
          </Box>
        </SimpleGrid>
      </Container>
    </Box>
  );
};

export default TestimonialSlider;